import { documentId, getDoc, getDocs, serverTimestamp } from 'firebase/firestore';
import { getDownloadURL, uploadBytes } from 'firebase/storage';
import { useContext, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { Context } from '../../../index';

import useUsername from './useUsername';

const useSendMessage = ({ activeChat, userId, replyMessage, setReplyMessage }) => {
  const [sendingAudio, setSendingAudio] = useState(false);
  const { firestore, functions, storage } = useContext(Context);
  const userName = useUsername(userId);

  const getChatData = async () => getDoc(firestore.collection('chat_rooms').doc(activeChat));

  const sendPush = async (messageText, newUsers) => {
    const chat = await getChatData();
    const userIds = chat.data()?.user_ids;
    Object.keys(newUsers).forEach((key) => {
      newUsers[key] = newUsers[key]?.unread_msg_nbr;
    });
    if (userIds?.length) {
      const otherUserIds = userIds.filter((item) => item !== userId);
      const otherUsers = await getDocs(
        firestore.collection('users').where(documentId(), 'in', otherUserIds),
      );
      const fcmTokens = otherUsers.docs.map((item) => item.data()?.fcm_token);
      if (fcmTokens.length === 1) {
        const sendPush = functions.httpsCallable('sendPush');
        await sendPush({
          sender_name: userName,
          sender_id: userId,
          room_id: activeChat,
          text: messageText,
          fcm_token: fcmTokens[0],
          users_id: newUsers,
          push_type: 1,
        });
      } else if (fcmTokens.length > 1) {
        const fcmTokensHash = {};
        otherUsers.docs.forEach((item) => {
          fcmTokensHash[item.id] = item.data()?.fcm_token;
        });
        const sendGroupPush = functions.httpsCallable('sendGroupPush');
        await sendGroupPush({
          sender_name: userName,
          sender_id: userId,
          room_id: activeChat,
          room_name: chat.data()?.room_name || '',
          text: messageText,
          fcm_tokens: fcmTokensHash,
          push_type: 0,
          users_id: newUsers,
        });
      }
    }
  };

  const createMessage = (messageText, attachments) => {
    const body = {
      msg_text: messageText,
      sender_id: userId,
      sender_name: userName,
      type: 0,
      status: 0,
      msg_epoch: serverTimestamp(),
    };
    if (replyMessage) {
      setReplyMessage?.(null);
    }
    firestore
      .collection('chat_rooms')
      .doc(activeChat)
      .collection('chat_messages')
      .add({
        ...body,
        ...(attachments ? { attachments } : {}),
        ...(replyMessage
          ? {
              reply_message: { ...replyMessage },
            }
          : {}),
      });
  };

  const getUsersUnreadMessages = async () => {
    const chat = await getChatData();
    const users = chat.data()?.users;
    Object.keys(users)?.forEach((key) => {
      if (key !== userId && typeof users?.[key]?.unread_msg_nbr === 'number') {
        users[key].unread_msg_nbr += 1;
      }
    });
    return users;
  };

  const updateChatRoomsData = (messageText, newUsers) =>
    firestore.collection('chat_rooms').doc(activeChat).update({
      last_msg_epoch: serverTimestamp(),
      last_msg: messageText,
      last_msg_sender: userId,
      users: newUsers,
    });

  const sendMessage = async (messageText, attachments) => {
    if (!activeChat) return;
    try {
      createMessage(messageText, attachments);
      const newUsers = await getUsersUnreadMessages();
      updateChatRoomsData(messageText, newUsers);
      await sendPush(messageText, newUsers);
    } catch (e) {
      console.log('e', e);
    }
  };

  const sendAudio = (file) => {
    setSendingAudio(true);
    const extension = 'wave';
    const fileName = `${userId}_${uuidv4()}.${extension}`;
    const ref = storage.ref(`${userId}/${fileName}`);
    uploadBytes(ref, file)
      .then((snapshot) =>
        getDownloadURL(snapshot.ref).then((downloadURL) => {
          const attachments = [
            {
              mimeType: 'audio/wave',
              name: fileName,
              path: downloadURL,
              size: snapshot.metadata.size,
            },
          ];
          sendMessage('', attachments);
        }),
      )
      .catch(() => alert('Uploading failed'))
      .finally(() => setSendingAudio(false));
  };

  const sendFile = (file) => {
    const extension = file.name.split('.')?.pop();
    const fileName = `${userId}_${uuidv4()}.${extension}`;
    const ref = storage.ref(`${userId}/${fileName}`);
    uploadBytes(ref, file)
      .then((snapshot) =>
        getDownloadURL(snapshot.ref).then((downloadURL) => {
          const attachments = [
            {
              mimeType: file.type,
              name: fileName,
              path: downloadURL,
              size: snapshot.metadata.size,
            },
          ];
          sendMessage('', attachments);
        }),
      )
      .catch(() => alert('Uploading failed'));
  };

  return {
    sendMessage,
    sendFile,
    sendAudio,
    sendingAudio,
  };
};

export default useSendMessage;
